import * as React from 'react'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import { Outlet, useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { CalendarEmployeesDocument, CalendarPageQueryDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import Filters from 'modules/core/components/Card/Filters'
import { DateNavigator } from 'modules/core/components/DateNavigator'
import FILTER_OPTIONS from 'modules/core/components/Filters/filterOptions'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useGraphQLClient } from 'modules/core/lib/graphql/provider'
import { track } from 'modules/core/lib/metrics'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import ExpandButton from 'modules/calendar/components/Calendar/components/ExpandButton'
import 'modules/calendar/components/Calendar/styles/calendarOverride.module.css'
import useCalendarComponents from 'modules/calendar/components/Calendar/useCalendarComponents'
import AddBulkTimeOffButton from 'modules/calendar/components/CalendarRoot/AddBulkTimeOffButton'
import Exports from 'modules/calendar/components/Exports'
import { useCalendarLeaves } from 'modules/calendar/hooks/useCalendarLeaves'
import type { CalendarEmployeeLookup } from 'modules/calendar/lib/calendarEmployeeData'
import type { RouteContextParams } from 'modules/calendar/lib/RouteContextParams'
import generateFetchData from 'modules/calendar/lib/generateFetchData'
import calendarMuncher from 'modules/calendar/lib/munchers'
import calendarEvents from 'modules/calendar/store/collections/calendarEvents'
import type CalendarEvent from 'modules/calendar/store/models/CalendarEvent'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

export const filterLeavesOnly = (calendarEventList: CalendarEvent[]): CalendarEvent[] => {
  return calendarEventList.filter((ev) => ev.attributes.type === 'leave')
}

const CalendarPage = () => {
  const { date, filtersForm, loading, range, onDateRangeChange, canSeeCalendar } =
    useOutletContext<RouteContextParams>()

  const { lastLeaveUpdatedInMilliseconds } = useCalendarLeaves()
  const localizer = momentLocalizer(moment)
  const filterByValue = filtersForm.get('filterBy').value
  const graphQLClient = useGraphQLClient()

  const { data } = useQuery(CalendarPageQueryDocument, {})
  const canSeeClockedIn = Boolean(data?.attendance._policies.companySeeClockedIn.can)
  const canEditBankHolidays = Boolean(data?.locations._policies.locationManage.can)
  const { canManageTimeoff, employee } = useCurrentSession()

  const canCalendarAccess =
    Boolean(data?.attendance._policies.companyCalendarAccess.can) && canSeeCalendar

  useUnsafeEffect(() => {
    track(graphQLClient, 'CalendarVisited')
  }, [graphQLClient])

  const allEvents = calendarEvents.toArray()
  const employeeIds = React.useMemo(() => {
    const ids = new Set<string>()
    for (const event of allEvents) {
      const eid = event.metadata('employee_id')
      if (eid) ids.add(eid)
    }
    return Array.from(ids)
  }, [allEvents])

  const { data: employeesData } = useQuery(CalendarEmployeesDocument, {
    variables: { ids: employeeIds },
    skip: employeeIds.length === 0,
  })

  const employeeLookup: CalendarEmployeeLookup = React.useMemo(() => {
    const map: CalendarEmployeeLookup = new Map()
    if (employeesData) {
      for (const edge of employeesData.employees.employeesConnection.edges) {
        map.set(edge.node.id, edge.node)
      }
    }
    return map
  }, [employeesData])

  const munchedData =
    filterByValue === FILTER_OPTIONS.employees.away
      ? calendarMuncher(filterLeavesOnly(allEvents), employeeLookup)
      : calendarMuncher(allEvents, employeeLookup)

  const calendarComponents = useCalendarComponents({
    munchedData,
    canEditBankHolidays,
    employeeLookup,
  })

  const [, setLocalFilterByValue] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.calendarFilter)

  const { loading: changingData } = useAsyncData(() => {
    const fetchData = generateFetchData(date, filterByValue, true)
    setLocalFilterByValue(filterByValue)

    return calendarEvents.fetch({
      data: fetchData,
    })
  }, [date, filterByValue, lastLeaveUpdatedInMilliseconds])

  return (
    <ResponsivePage.Body
      showHeader={false}
      authorized={canCalendarAccess}
      loading={loading || changingData}
      error={null}
    >
      <Box gap="s16">
        <Box flexDirection="row" justifyContent="spaceBetween" height="s32">
          <Filters
            form={filtersForm}
            showFilterByTeam
            showFilterByAway
            showFilterByAwayThisMonth
            showFilterByTimeoffSupervisor={!!employee}
            showFilterByDirectManagedEmployee={!!employee}
            showFilterByManagedEmployee={!!employee}
            canSeeClockedIn={canSeeClockedIn}
          />

          <Box gap="s16" flexDirection="row">
            <DateNavigator
              regularPeriods={['month']}
              startDate={range[0]}
              endDate={range[1]}
              withRelativeDates={false}
              onDateRangeChange={(...newRange) => onDateRangeChange(newRange)}
            />
            <Exports />
            {canManageTimeoff && <AddBulkTimeOffButton />}
          </Box>
        </Box>
        <Calendar
          toolbar={false}
          localizer={localizer}
          views={['month']}
          selectable={false}
          date={date.toDate()}
          messages={{
            showMore: function renderExpandButton(): any {
              return <ExpandButton direction="up" />
            },
          }}
          events={munchedData.calendarEvents}
          components={calendarComponents as any}
        />
        <Outlet />
      </Box>
    </ResponsivePage.Body>
  )
}

export default observer(CalendarPage)
